/**
 * Mailer Campaign List Hook
 */
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getMailerCampaigns } from '../services'
import {
    getMailerCampaignColumns,
    MAILER_CAMPAIGN_FILTER_DEFAULTS,
    MAILER_CAMPAIGN_SORT_CONFIG,
} from '../utils/mailer-campaign-list-config'

interface CampaignFilters {
    q: string
}

export const useMailerCampaignList = () => {
    const { t } = useTranslation()

    const { params, queryParams, setParams, searchInputProps } =
        useUrlSearchParams<CampaignFilters>({
            defaults: {
                page: 1,
                page_size: 20,
                ...MAILER_CAMPAIGN_SORT_CONFIG.defaultSort,
            },
            filterDefaults: MAILER_CAMPAIGN_FILTER_DEFAULTS,
        })

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.MAILER_CAMPAIGNS, queryParams],
        queryFn: () =>
            getMailerCampaigns({
                page: params.page,
                page_size: params.page_size,
                search: queryParams.q,
            }),
        placeholderData: true,
    })

    const campaigns = data?.results || []
    const pagination = data?.pagination

    const columns = useMemo(() => getMailerCampaignColumns(t), [t])

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (size: number) => setParams({ page_size: size }),
        [setParams]
    )
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    return {
        campaigns,
        pagination,
        stateComponent: renderState(),
        params,
        searchInputProps,
        columns,
        handlePageChange,
        handlePageSizeChange,
        handleSortChange,
        t,
    }
}
